import { TabContext, TabPanel } from "@mui/lab";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { MomentCardDropArea } from "../../../modules/moment-card/components/drag-and-drop/moment-card-drop-area";
import { MomentDashboardSection } from "../../../modules/moment-dashboard-section/moment-dashboard-section";
import { SearchField } from "../../../modules/search/search-field";
import { GradientMegaphone } from "../../../styles/icons/gradient-megaphone";
import { makeDefaultFilters } from "../../../utils/default-filters";

import type { MomentCardMomentActivity } from "../../../modules/moment/server/supabase-moment-service";
import type { FC } from "react";

export const MomentCarousels: FC<{
  recentlyEdited?: MomentCardMomentActivity[];
  sent?: MomentCardMomentActivity[];
  upcoming?: MomentCardMomentActivity[];
}> = ({ recentlyEdited, sent, upcoming }) => {
  const [value, setValue] = useState("");

  const [selectedTab, setSelectedTab] = useState("recently_edited");
  const navigate = useNavigate();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(e.target.value);
  }, []);

  const handleSubmit = useCallback((): void => {
    const searchParams = makeDefaultFilters();
    searchParams.set("q", value);

    void navigate({
      pathname: "/tabbed-programs",
      search: searchParams.toString(),
    });
  }, [navigate, value]);

  return (
    <MomentCardDropArea>
      <Box sx={{ minWidth: 200, maxWidth: 510, mt: 4 }}>
        <Box sx={{ pb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, display: "flex", gap: 1, color: "text.primary" }}
          >
            <GradientMegaphone />
            Communications
          </Typography>
        </Box>
      </Box>
      <TabContext value={selectedTab}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, v: string) => setSelectedTab(v)}
            sx={{ flexShrink: 1 }}
          >
            <Tab key="tab-recently-edited" value="recently_edited" label="Recently Edited" />
            <Tab key="tab-sent" value="sent" label="Sent" />
            <Tab key="tab-upcoming" value="upcoming" label="Upcoming" />
          </Tabs>
          <Box
            sx={{
              minWidth: 80,
              maxWidth: 330,
              width: "100%",
              flexGrow: 1,
              flexBasis: { xs: "100%", sm: "auto" },
            }}
          >
            <SearchField
              onSubmit={handleSubmit}
              onChange={handleChange}
              defaultValue={value}
              label="Search Communications & Programs"
            />
          </Box>
        </Box>
        <TabPanel value="recently_edited" sx={{ minHeight: 570 }}>
          <MomentDashboardSection
            moments={recentlyEdited}
            title="Recently Edited"
            hideTitle
            sx={{ mt: 5 }}
          />
        </TabPanel>
        <TabPanel value="sent" sx={{ minHeight: 570 }}>
          <MomentDashboardSection moments={sent} title="Sent" hideTitle sx={{ mt: 5 }} />
        </TabPanel>
        <TabPanel value="upcoming" sx={{ minHeight: 570 }}>
          <Typography color="textSecondary">
            Does not include Trigger-Based Communications
          </Typography>
          <MomentDashboardSection moments={upcoming} title="Upcoming" hideTitle sx={{ mt: 5 }} />
        </TabPanel>
      </TabContext>
    </MomentCardDropArea>
  );
};
